import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import {
  Newspaper, ExternalLink, Shield, Clock, ArrowUpDown,
  Play, RefreshCw, Plus, Trash2, Save, X, CheckCircle, AlertCircle,
  Globe, Search, Sparkles
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LANG_MAP = { en: 'en', de: 'de', sk: 'sk', cs: 'cs' };

const TrustBadge = ({ score }) => {
  const color = score >= 70 ? 'bg-green-100 text-green-700' : score >= 40 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700';
  return <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${color}`}>{score}</span>;
};

const NewsModule = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isEditor = user?.role === 'editor' || isAdmin;
  const lang = LANG_MAP[language] || 'en';

  const [articles, setArticles] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [countries, setCountries] = useState([]);
  const [sortBy, setSortBy] = useState('recency');
  const [filterCountry, setFilterCountry] = useState('all');
  const [filterHashtag, setFilterHashtag] = useState('');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const LIMIT = 12;

  // Admin state
  const [sources, setSources] = useState([]);
  const [pipelineStatus, setPipelineStatus] = useState(null);
  const [pipelineLoading, setPipelineLoading] = useState(false);
  const [adminView, setAdminView] = useState('feed');
  const [showAddSource, setShowAddSource] = useState(false);
  const [newSource, setNewSource] = useState({ name: '', url: '', rss_url: '', trust_score: 70 });
  const [notification, setNotification] = useState(null);
  const [discoveredSources, setDiscoveredSources] = useState([]);
  const [discovering, setDiscovering] = useState(false);

  const showNotif = (msg, type = 'success') => {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const getLocalized = (article, field) => {
    return article[`${field}_${lang}`] || article[`${field}_sk`] || article[`${field}_en`] || article[field] || article.headline_original || '';
  };

  const fetchArticles = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ sort: sortBy, limit: LIMIT, skip: page * LIMIT });
      if (filterCountry !== 'all') params.set('country', filterCountry);
      if (filterHashtag) params.set('hashtag', filterHashtag);
      const res = await fetch(`${API_URL}/api/news/feed?${params}`);
      if (res.ok) { const d = await res.json(); setArticles(d.articles); setTotal(d.total); }
    } catch { /* logged server-side */ }
    setLoading(false);
  }, [sortBy, filterCountry, filterHashtag, page]);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/news/daily-summary`);
      if (res.ok) { const d = await res.json(); if (d.summary_sk || d.summary_en) setSummary(d); }
    } catch { /* logged server-side */ }
  }, []);

  const fetchCountries = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/news/countries`);
      if (res.ok) { const d = await res.json(); setCountries(d.countries || []); }
    } catch { /* logged server-side */ }
  }, []);

  const fetchAdminData = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const [srcRes, pipeRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/news/sources`, { credentials: 'include' }),
        fetch(`${API_URL}/api/admin/news/pipeline-status`, { credentials: 'include' })
      ]);
      if (srcRes.ok) { const d = await srcRes.json(); setSources(d.sources || []); }
      if (pipeRes.ok) { const d = await pipeRes.json(); setPipelineStatus(d); }
    } catch { /* logged server-side */ }
  }, [isAdmin]);

  useEffect(() => { fetchArticles(); }, [fetchArticles]);
  useEffect(() => { fetchSummary(); fetchCountries(); fetchAdminData(); }, [fetchSummary, fetchCountries, fetchAdminData]);

  const triggerPipeline = async () => {
    setPipelineLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/news/trigger-pipeline`, { method: 'POST', credentials: 'include' });
      if (res.ok) showNotif(t('news.pipelineStarted'));
      const poll = setInterval(async () => {
        const sr = await fetch(`${API_URL}/api/admin/news/pipeline-status`, { credentials: 'include' });
        if (sr.ok) {
          const d = await sr.json();
          setPipelineStatus(d);
          if (d.status !== 'running') {
            clearInterval(poll);
            setPipelineLoading(false);
            fetchArticles(); fetchSummary(); fetchCountries();
          }
        }
      }, 3000);
    } catch { setPipelineLoading(false); showNotif('Error', 'error'); }
  };

  const addSource = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/news/sources`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newSource)
      });
      if (res.ok) { showNotif('Source added'); setShowAddSource(false); setNewSource({ name: '', url: '', rss_url: '', trust_score: 70 }); fetchAdminData(); }
    } catch { showNotif('Error', 'error'); }
  };

  const deleteSource = async (id) => {
    if (!window.confirm('Delete this source?')) return;
    await fetch(`${API_URL}/api/admin/news/sources/${id}`, { method: 'DELETE', credentials: 'include' });
    fetchAdminData();
  };

  const deleteArticle = async (id) => {
    if (!window.confirm('Delete this article?')) return;
    await fetch(`${API_URL}/api/admin/news/articles/${id}`, { method: 'DELETE', credentials: 'include' });
    fetchArticles();
  };

  const discoverSources = async () => {
    setDiscovering(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/news/discover-sources`, { method: 'POST', credentials: 'include' });
      if (res.ok) {
        const d = await res.json();
        setDiscoveredSources(d.suggestions || []);
        if (d.suggestions?.length === 0) showNotif('No new sources found', 'error');
      }
    } catch { showNotif('Error', 'error'); }
    setDiscovering(false);
  };

  const approveSource = async (src) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/news/approve-source`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(src)
      });
      if (res.ok) {
        showNotif(`Added: ${src.name}`);
        setDiscoveredSources(prev => prev.filter(s => s.name !== src.name));
        fetchAdminData();
      }
    } catch { showNotif('Error', 'error'); }
  };

  const handleHashtagClick = (tag) => {
    if (filterHashtag === tag) {
      setFilterHashtag('');
    } else {
      setFilterHashtag(tag);
    }
    setPage(0);
  };

  const formatDate = (d) => {
    try { return new Date(d).toLocaleDateString('sk-SK', { day: 'numeric', month: 'short', year: 'numeric' }); }
    catch { return d; }
  };

  const summaryText = summary ? (summary[`summary_${lang}`] || summary.summary_sk || summary.summary_en || '') : '';

  return (
    <div data-testid="news-module">
      {notification && (
        <div className={`fixed top-24 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-sm ${notification.type === 'error' ? 'bg-red-500 text-white' : 'bg-green-500 text-white'}`}>
          {notification.type === 'error' ? <AlertCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
          {notification.msg}
        </div>
      )}

      {/* Admin Controls */}
      {isAdmin && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-[#0d1442] flex items-center gap-2"><Shield className="w-4 h-4" /> {t('news.adminControls')}</h3>
            <div className="flex gap-2">
              <button onClick={() => setAdminView('feed')} className={`px-3 py-1.5 text-xs rounded-lg ${adminView === 'feed' ? 'bg-[#1a237e] text-white' : 'bg-slate-100 text-slate-600'}`}>{t('news.title')}</button>
              <button onClick={() => setAdminView('sources')} className={`px-3 py-1.5 text-xs rounded-lg ${adminView === 'sources' ? 'bg-[#1a237e] text-white' : 'bg-slate-100 text-slate-600'}`}>{t('news.manageSources')}</button>
            </div>
          </div>

          {/* Pipeline Status */}
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg mb-4">
            <div className="text-sm">
              <span className="font-medium text-slate-700">{t('news.lastRun')}: </span>
              <span className="text-slate-500">
                {pipelineStatus?.status === 'running'
                  ? <span className="text-blue-600 font-medium">{pipelineStatus.progress}</span>
                  : pipelineStatus?.completed_at ? formatDate(pipelineStatus.completed_at) + ' — ' + pipelineStatus.progress : t('news.neverRun')}
              </span>
            </div>
            <button data-testid="trigger-pipeline-btn" onClick={triggerPipeline} disabled={pipelineLoading}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-[#1a237e] text-white rounded-lg hover:bg-[#0d1442] disabled:opacity-50">
              {pipelineLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              {t('news.triggerPipeline')}
            </button>
          </div>

          {/* Sources Management */}
          {adminView === 'sources' && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-slate-700">{t('news.manageSources')} ({sources.length})</h4>
                <div className="flex gap-2">
                  <button onClick={discoverSources} disabled={discovering}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs border border-[#1a237e] text-[#1a237e] rounded-lg hover:bg-[#1a237e]/5 disabled:opacity-50">
                    {discovering ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                    {t('news.discoverSources')}
                  </button>
                  <button onClick={() => setShowAddSource(!showAddSource)} className="flex items-center gap-1 px-3 py-1.5 text-xs bg-[#1a237e] text-white rounded-lg">
                    <Plus className="w-3 h-3" /> {t('news.addSource')}
                  </button>
                </div>
              </div>

              {/* Discovered sources suggestions */}
              {discoveredSources.length > 0 && (
                <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-xs font-semibold text-amber-700 mb-2">{t('news.suggestedSources')}</p>
                  {discoveredSources.map((s, i) => (
                    <div key={s.name} className="flex items-center justify-between p-2 bg-white rounded mb-1">
                      <div className="text-sm">
                        <span className="font-medium">{s.name}</span>
                        <span className="text-xs text-slate-500 ml-2">{s.url}</span>
                      </div>
                      <button onClick={() => approveSource(s)} className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700">
                        <CheckCircle className="w-3 h-3 inline mr-1" />Approve
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {showAddSource && (
                <div className="grid grid-cols-4 gap-2 mb-3 p-3 bg-blue-50 rounded-lg">
                  <input placeholder={t('news.sourceName')} value={newSource.name} onChange={e => setNewSource(p => ({...p, name: e.target.value}))} className="px-3 py-2 text-sm border rounded-lg" />
                  <input placeholder={t('news.sourceUrl')} value={newSource.url} onChange={e => setNewSource(p => ({...p, url: e.target.value}))} className="px-3 py-2 text-sm border rounded-lg" />
                  <input placeholder={t('news.sourceRss')} value={newSource.rss_url} onChange={e => setNewSource(p => ({...p, rss_url: e.target.value}))} className="px-3 py-2 text-sm border rounded-lg" />
                  <div className="flex gap-2">
                    <input type="number" placeholder="Trust" value={newSource.trust_score} onChange={e => setNewSource(p => ({...p, trust_score: parseInt(e.target.value) || 0}))} className="px-3 py-2 text-sm border rounded-lg w-20" />
                    <button onClick={addSource} className="px-3 py-2 text-sm bg-green-600 text-white rounded-lg"><Save className="w-4 h-4" /></button>
                    <button onClick={() => setShowAddSource(false)} className="px-3 py-2 text-sm bg-slate-200 rounded-lg"><X className="w-4 h-4" /></button>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                {sources.map(src => (
                  <div key={src.source_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <TrustBadge score={src.trust_score} />
                      <div>
                        <span className="text-sm font-medium text-[#0d1442]">{src.name}</span>
                        <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${src.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{src.status}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      {src.last_crawled && <span>Last: {formatDate(src.last_crawled)}</span>}
                      <button onClick={() => deleteSource(src.source_id)} className="p-1 text-red-500 hover:bg-red-50 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Daily Summary */}
      {summaryText && (
        <div data-testid="daily-summary" className="bg-gradient-to-r from-[#1a237e] to-[#0d1442] rounded-xl p-6 mb-6 text-white">
          <div className="flex items-center gap-2 mb-3">
            <Newspaper className="w-5 h-5" />
            <h3 className="font-bold">{t('news.dailySummary')}</h3>
            <span className="text-xs text-white/60 ml-auto">{summary?.date}</span>
          </div>
          <p className="text-sm text-white/90 leading-relaxed">{summaryText}</p>
        </div>
      )}

      {/* Sort & Filter Bar */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2">
          <ArrowUpDown className="w-4 h-4 text-slate-400" />
          <select data-testid="sort-select" value={sortBy} onChange={e => { setSortBy(e.target.value); setPage(0); }}
            className="text-sm border-none outline-none bg-transparent text-slate-700">
            <option value="recency">{t('news.recency')}</option>
            <option value="trust_score">{t('news.trustScore')}</option>
          </select>
        </div>
        <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2">
          <Globe className="w-4 h-4 text-slate-400" />
          <select data-testid="country-filter" value={filterCountry} onChange={e => { setFilterCountry(e.target.value); setPage(0); }}
            className="text-sm border-none outline-none bg-transparent text-slate-700">
            <option value="all">{t('news.allCountries')}</option>
            {countries.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        {filterHashtag && (
          <button onClick={() => { setFilterHashtag(''); setPage(0); }}
            className="flex items-center gap-1 px-3 py-2 text-sm bg-[#1a237e] text-white rounded-lg">
            {filterHashtag} <X className="w-3 h-3" />
          </button>
        )}
        <span className="text-sm text-slate-500 ml-auto">{total} {t('news.articlesLabel')}</span>
      </div>

      {/* Articles Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin w-8 h-8 border-4 border-[#1a237e] border-t-transparent rounded-full" />
        </div>
      ) : articles.length === 0 ? (
        <div data-testid="no-articles" className="text-center py-16 text-slate-500">
          <Newspaper className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <p className="text-lg font-medium mb-2">{t('news.noArticles')}</p>
          {isAdmin && <p className="text-sm">{t('news.triggerHint')}</p>}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {articles.map(a => (
            <article key={a.article_id} data-testid="news-article-card" className="bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden flex flex-col">
              <div className="p-5 flex-1">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-medium text-slate-500">{a.source_name}</span>
                  <TrustBadge score={a.trust_score} />
                </div>
                <h3 className="font-bold text-[#0d1442] text-sm leading-snug mb-3 line-clamp-3">
                  {getLocalized(a, 'headline')}
                </h3>
                <p className="text-xs text-slate-600 leading-relaxed mb-3 line-clamp-4">
                  {getLocalized(a, 'perex')}
                </p>
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {(a.hashtags || []).map((tag, i) => (
                    <button key={tag} onClick={() => handleHashtagClick(tag)}
                      className={`px-2 py-0.5 text-xs font-medium rounded-full transition-colors cursor-pointer ${
                        filterHashtag === tag ? 'bg-[#1a237e] text-white' : 'bg-[#1a237e]/10 text-[#1a237e] hover:bg-[#1a237e]/20'
                      }`}>
                      {tag}
                    </button>
                  ))}
                </div>
              </div>
              <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between">
                <span className="text-xs text-slate-400">{formatDate(a.timestamp)}</span>
                <div className="flex items-center gap-2">
                  {isEditor && (
                    <button onClick={() => deleteArticle(a.article_id)} className="p-1 text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                  )}
                  <a href={a.original_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-[#1a237e] font-medium hover:underline">
                    {t('news.readMore')} <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {total > LIMIT && (
        <div className="flex items-center justify-center gap-4 mt-8">
          <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0} className="px-4 py-2 text-sm border rounded-lg disabled:opacity-50 hover:bg-slate-50">{t('news.prev')}</button>
          <span className="text-sm text-slate-500">{page + 1} / {Math.ceil(total / LIMIT)}</span>
          <button onClick={() => setPage(page + 1)} disabled={(page + 1) * LIMIT >= total} className="px-4 py-2 text-sm border rounded-lg disabled:opacity-50 hover:bg-slate-50">{t('news.next')}</button>
        </div>
      )}
    </div>
  );
};

export default NewsModule;
